import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DocumentTextIcon, ViewColumnsIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { StatusDot } from '@redpanda/ui';
import { listReturnUrl, RESTORE_SCROLL_STATE } from '@redpanda/utils';
import { projectTone, toneOf } from '../utils/statusTone';

type ProjectView = 'detail' | 'kanban' | 'gantt';

interface ProjectHeaderProps {
  projectId: string;
  project?: { name: string; status?: string } | null;
  current: ProjectView;
  /** 標題列右側的主要按鈕（每個畫面最多 1 顆） */
  action?: React.ReactNode;
  /** 詳情頁目前的分頁：切到看板、甘特圖時帶著走（?returnTab=），按「詳情」回到同一個分頁 */
  returnTab?: string;
}

/**
 * 專案詳情／看板／甘特圖共用的頁首（§3-13、R20、R24）
 * - 麵包屑取代「返回列表」按鈕；標題＝專案名稱＋狀態點
 * - 三個檢視是同一個專案的不同看法，用分段切換（目前檢視 accent 淡底），切換時頁首位置不動
 * - 分段切換有文字，放在標題列右側第一個（items-baseline 才對得齊）；主要按鈕在最右
 */
const ProjectHeader: React.FC<ProjectHeaderProps> = ({ projectId, project, current, action, returnTab }) => {
  const { t } = useTranslation(['translation', 'pages']);

  const keep = returnTab ? `?returnTab=${returnTab}` : '';
  const views = [
    { key: 'detail', to: `/projects/${projectId}${returnTab ? `?tab=${returnTab}` : ''}`, label: t('pmProjects.views.detail', { ns: 'pages' }), Icon: DocumentTextIcon },
    { key: 'kanban', to: `/kanban/${projectId}${keep}`, label: t('pmProjects.views.kanban', { ns: 'pages' }), Icon: ViewColumnsIcon },
    { key: 'gantt', to: `/gantt/${projectId}${keep}`, label: t('pmProjects.views.gantt', { ns: 'pages' }), Icon: ChartBarIcon },
  ] as const;

  const tabClass = (active: boolean) =>
    `inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm transition-colors ${
      active ? 'bg-accent-soft text-accent font-medium' : 'text-fg-secondary hover:text-fg'
    }`;

  return (
    <>
      <nav className="mb-1 text-[13px] text-fg-secondary">
        <Link to={listReturnUrl('/projects')} state={RESTORE_SCROLL_STATE} className="hover:text-fg transition-colors">{t('pmProjects.title', { ns: 'pages' })}</Link>
        <span className="mx-1.5 text-fg-muted">/</span>
      </nav>
      <div className="mb-4">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-3">
          <div className="flex items-baseline gap-3 min-w-0">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white truncate">{project?.name || ' '}</h1>
            {project?.status && (
              <StatusDot tone={toneOf(projectTone, project.status)} label={t(`pmProjects.status.${project.status}`, { ns: 'pages' })} />
            )}
          </div>
          <div className="flex items-baseline gap-3">
            <nav aria-label={t('pmProjects.views.label', { ns: 'pages' })} className="inline-flex h-10 items-center rounded-md border border-line bg-surface p-0.5">
              {views.map(({ key, to, label, Icon }) => (
                <Link key={key} to={to} aria-current={current === key ? 'page' : undefined} className={tabClass(current === key)}>
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {label}
                </Link>
              ))}
            </nav>
            {action}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectHeader;
